import { CaretDownIcon, MailIcon } from 'assets/icons';
import { SHOW_LIMITED_CONTRIBUTOR } from 'common/share-dialog/roles';
import { VaultCollaboratorRole } from 'common/enums';
import { Modal } from 'common/modal';
import { classnames, isEmailValid } from 'helpers/utils';
import { useState } from 'react';
import styles from './collaborators-modal.module.scss';

type CollaboratorsModalProps = {
  onAdd: (collaborator: {
    name: string;
    email: string;
    role: VaultCollaboratorRole;
    canViewVault: boolean;
  }) => void;
  onClose: () => void;
};

const ROLE_OPTIONS: {
  value: VaultCollaboratorRole;
  label: string;
  explanation: string;
}[] = [
  {
    value: VaultCollaboratorRole.viewer,
    label: 'Viewer',
    explanation: "Can see what isn't private, but can't change anything.",
  },
  {
    value: VaultCollaboratorRole.contributor,
    label: 'Contributor',
    explanation: "Can add content, but can't edit or delete existing content.",
  },
  {
    value: VaultCollaboratorRole.editor,
    label: 'Editor',
    explanation: 'Can add, edit and delete content throughout this Vault.',
  },
];

// One collaborator at a time. Nothing is sent from here — the collaborator is
// held in the wizard's state and invited once the vault is paid for, which is
// what the footer note is telling the user.
const CollaboratorsModal: React.FC<CollaboratorsModalProps> = ({
  onAdd,
  onClose,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<VaultCollaboratorRole>(
    VaultCollaboratorRole.contributor,
  );
  const [canViewVault, setCanViewVault] = useState(true);
  const [emailTouched, setEmailTouched] = useState(false);

  const emailError =
    emailTouched && email.trim() !== '' && !isEmailValid(email.trim());
  const canSubmit = name.trim() !== '' && isEmailValid(email.trim());

  const roleExplanation = ROLE_OPTIONS.find(
    (option) => option.value === role,
  )!.explanation;

  const handleAdd = () => {
    if (!canSubmit) return;

    onAdd({
      name: name.trim(),
      email: email.trim(),
      role,
      canViewVault: role === VaultCollaboratorRole.editor || canViewVault,
    });
  };

  const showHeader = () => (
    <div className={styles.headerText}>
      <span className={styles.title}>Add collaborator</span>
      <span className={styles.subtitle}>
        Add someone to help with this Vault.
      </span>
    </div>
  );

  return (
    <Modal
      title={showHeader()}
      onClose={onClose}
      closeOnOutsideClick
      contentClassName={styles.modalContent}
    >
      <div className={styles.fields}>
        <label className={styles.field} htmlFor="collaborator-name">
          <span className={styles.label}>Name</span>
          <input
            id="collaborator-name"
            type="text"
            className={styles.input}
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className={styles.field} htmlFor="collaborator-email">
          <span className={styles.label}>Email</span>
          <input
            id="collaborator-email"
            type="email"
            className={classnames(styles.input, {
              [styles.inputError]: emailError,
            })}
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
          />
          {emailError && (
            <span className={styles.errorText}>
              Enter a valid email address
            </span>
          )}
        </label>

        <div className={styles.field}>
          <span className={styles.label}>Permission level</span>
          {/* Native select styled as the design's pill dropdown — keeps
              keyboard and mobile behaviour for free. */}
          <div className={styles.selectWrap}>
            <select
              className={styles.select}
              value={role}
              aria-label="Permission level"
              onChange={(e) => setRole(e.target.value as VaultCollaboratorRole)}
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <CaretDownIcon className={styles.selectChevron} />
          </div>
          <p className={styles.roleExplanation}>{roleExplanation}</p>
        </div>

        {/* Editors see the whole vault by definition; only Contributors can
            be limited to their own additions. Hidden until the limited
            contributor role is agreed on (see common/share-dialog/roles.ts). */}
        {SHOW_LIMITED_CONTRIBUTOR &&
          role === VaultCollaboratorRole.contributor && (
            <div className={styles.visibility}>
              <button
                type="button"
                role="switch"
                aria-checked={canViewVault}
                aria-label="Can view the Vault"
                className={classnames(styles.toggle, {
                  [styles.toggleOn]: canViewVault,
                })}
                onClick={() => setCanViewVault((current) => !current)}
              >
                <span className={styles.toggleKnob} />
              </button>
              <div className={styles.visibilityText}>
                <span className={styles.visibilityTitle}>
                  Can view the Vault
                </span>
                <span className={styles.visibilityDescription}>
                  Otherwise, they&apos;ll only see their own additions.
                </span>
              </div>
            </div>
          )}
      </div>

      <div className={styles.actions}>
        <div className={styles.info}>
          <span className={styles.infoIcon}>
            <MailIcon />
          </span>
          <p className={styles.infoText}>
            Invites will send
            <br />
            when Vault is created.
          </p>
        </div>

        <button
          type="button"
          className={styles.submitButton}
          disabled={!canSubmit}
          onClick={handleAdd}
        >
          Add collaborator
        </button>
      </div>
    </Modal>
  );
};

export { CollaboratorsModal };
